import React, { useState } from 'react';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { FormInput, FormCheckbox } from '@common/ui/form';
import Button from '@/common/ui/components/Button';

export default function SmsOptin() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!phoneNumber.trim()) {
      setError('Phone number is required');
      return;
    }

    if (!smsConsent) {
      setError('Please consent to SMS notifications to proceed');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Find patient document by phone number
      const q = query(collection(db, 'patients'), where('phoneNumber', '==', phoneNumber));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError('Phone number not found. Please make sure you have completed the medication form first.');
        return;
      }

      const patientDoc = querySnapshot.docs[0];
      const patientData = patientDoc.data();

      // Update SMS consent
      await updateDoc(doc(db, 'patients', patientDoc.id), {
        smsConsent: true,
        smsOptInDate: new Date(),
        smsOptInMethod: 'opt-in-page',
        updatedAt: new Date()
      });

      // Also update medications array if it exists
      if (patientData.medications && Array.isArray(patientData.medications)) {
        const updatedMedications = patientData.medications.map((med: any) => ({
          ...med,
          smsConsent: true
        }));

        await updateDoc(doc(db, 'patients', patientDoc.id), {
          medications: updatedMedications
        });
      }

      setSubmitted(true);
    } catch (error) {
      setError('Error enabling SMS notifications. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-pure-white rounded-md shadow-card p-8 text-center">
          <div className="w-16 h-16 bg-wellness-mint/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-wellness-mint" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-title-lg text-gray-900 mb-4">SMS Alerts Activated! 📱</h2>
          <p className="text-body-lg text-text-secondary mb-4">
            You'll now receive instant SMS notifications when your medication is ready for pickup.
          </p>
          <p className="text-small text-text-secondary">
            Reply STOP to any message to unsubscribe at any time.
          </p>
          <div className="mt-6">
            <Button
              onClick={() => window.close()}
              variant="primary"
              size="md"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-pure-white rounded-md shadow-card p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-trust-blue/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-trust-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.001 8.001 0 01-7.938-7M3 12c0-4.418 3.582-8 8-8s8 3.582 8 8z" />
            </svg>
          </div>
          <h1 className="text-title-lg text-gray-900 mb-2">Enable SMS Alerts</h1>
          <p className="text-body-md text-text-secondary">
            Get faster notifications when your medication is ready
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <FormInput
            id="phoneNumber"
            label="Phone Number"
            type="tel"
            value={phoneNumber}
            onChange={setPhoneNumber}
            placeholder="(555) 123-4567"
            required
          />
          <p className="text-small text-text-secondary mt-1">
            Enter the phone number you provided during signup
          </p>

          <FormCheckbox
            id="smsConsent"
            checked={smsConsent}
            onChange={setSmsConsent}
            label={
              <span>
                I consent to receiving text message notifications from CampusMeds
                <span className="block text-small text-text-secondary mt-1">
                  Standard message and data rates may apply. You can opt out anytime by replying STOP.
                </span>
              </span>
            }
            required
          />

          {error && (
            <div className="text-error-red text-small">{error}</div>
          )}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Enabling SMS...' : 'Enable SMS Alerts'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-small text-text-secondary">
            You'll continue to receive email notifications regardless of SMS preference.
          </p>
        </div>
      </div>
    </div>
  );
}